import { useCallback, useEffect, useMemo, useState } from "react";
import { useApolloClient } from "@apollo/client/react";
import {
  Box,
  Dialog,
  DialogDescription,
  DialogHeader,
  DialogPopup,
  DialogTitle,
  Field,
  FieldDescription,
  FieldLabel,
  Input,
} from "@salestrak/ui";

import { WorkflowSelector } from "#app/components/entity-selectors";
import { VIEW_TYPE_META, viewVisual } from "#app/components/view-options";
import { WidgetsDocument } from "#app/lib/data";
import { useWorkspace } from "#app/lib/data/workspace-context";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import { useCreateEntityView } from "#app/lib/hooks/use-entity-views";
import { viewPath } from "#app/lib/views/view-routes";
import { useRecentDashboards } from "./use-recent-dashboards";
import { AnalyticsContext, DashboardEvent, useAnalytics } from "#app/lib/analytics";
import type { Omittable } from "@salestrak/ui";

export type NewDashboardDialogProps = Omittable<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The door that opened the dialog, for `dashboard.create`: "directory"
   *  or "nav_menu". */
  via: string;
}>;

/**
 * THE workspace-dashboard create flow — the Dashboards module menu's "+" and
 * the directory's Create Dashboard button both open this dialog. A workspace
 * dashboard is a dashboard-type entity view whose scope is the workspace
 * itself: it lives only in the Dashboards section, never in a workflow's tab
 * strip, and reads from the workflow SET it names. That set decides every
 * widget's data source, so it is the one thing settled before the row exists
 * (the server refuses a dashboard that reads from nothing). Nothing is
 * written until Create: closing the dialog leaves no draft behind, so the
 * directory never carries an unfinished row.
 *
 * Create makes the row and lands on it. The board starts blank on purpose —
 * its blank state is the template chooser, so "pick a starting point" happens
 * on the dashboard itself, with the source already known. The name is
 * optional: left blank, the server names the row by its type, and rename
 * lives in place afterwards like every saved view's.
 *
 * Anyone may create: the row is PRIVATE to its creator until they share it,
 * which is the gated act, on the page.
 */
export function NewDashboardDialog({ open, onOpenChange, via }: NewDashboardDialogProps) {
  const { track } = useAnalytics();
  const { activeWorkspace } = useWorkspace();
  const { workflows } = useWorkspaceData();
  const client = useApolloClient();
  const createEntityView = useCreateEntityView();
  const { recordVisit } = useRecentDashboards();
  const { push } = useAppNavigate();

  const [name, setName] = useState("");
  const [workflowIds, setWorkflowIds] = useState<string[]>([]);
  const [creating, setCreating] = useState(false);

  // An archived workflow's views are parked, so it is not a source to offer.
  const archivedIds = useMemo(
    () =>
      workflows.filter((workflow) => workflow.archived).map((w) => w.workflowId),
    [workflows],
  );

  // The dialog opens with both questions unanswered every time.
  useEffect(() => {
    if (!open) {
      setName("");
      setWorkflowIds([]);
      setCreating(false);
    }
  }, [open]);

  const canSubmit = workflowIds.length > 0 && !creating;

  const handleCreate = useCallback(async () => {
    const workspaceId = activeWorkspace?.workspaceId;
    if (!workspaceId || workflowIds.length === 0) return;
    setCreating(true);
    try {
      const scope = { kind: "workspace" as const, id: workspaceId };
      // Stamp the type's seed identity so the row wears the same tile the
      // tab-strip flow's dashboards do; the server would otherwise store its
      // colorless default.
      const meta = VIEW_TYPE_META.dashboard;
      const view = await createEntityView(scope, {
        type: "dashboard",
        name: name.trim() || undefined,
        icon: { icon: meta.icon, color: meta.color },
        visibility: "private",
        workflowIds,
      });
      // A failed create (already toasted by the hook) keeps the dialog open
      // with the answers standing, so the retry is one press.
      if (!view) return;
      track({
        context: AnalyticsContext.Dashboard,
        event: DashboardEvent.Create,
        data: { via, workflowCount: workflowIds.length, named: name.trim() !== "" },
      });
      // A dashboard someone added starts blank — the server seeds no default
      // board for it — so its widget list is known before it is ever read.
      // Writing that answer into the cache lets the page paint the template
      // chooser on its first render instead of a blank board for a round
      // trip (the store trusts an empty cached list for exactly this kind).
      if (view.widgetView) {
        client.writeQuery({
          query: WidgetsDocument,
          variables: { view: view.widgetView },
          data: { widgets: [] },
        });
      }
      // Opening it is a visit: the new dashboard heads Recent and carries a
      // Last viewed date the moment it exists.
      recordVisit({
        kind: "workspace",
        id: workspaceId,
        viewKey: view.id,
        name: view.name,
        ...viewVisual(view),
      });
      onOpenChange(false);
      push(viewPath(scope, view));
    } finally {
      setCreating(false);
    }
  }, [
    activeWorkspace?.workspaceId,
    workflowIds,
    name,
    createEntityView,
    client,
    recordVisit,
    onOpenChange,
    push,
    track,
    via,
  ]);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && creating) return;
        onOpenChange(next);
      }}
    >
      <DialogPopup
        size="md"
        dialogOptions={{
          primary: { label: "Create Dashboard", onClick: () => void handleCreate(), disabled: !canSubmit, loading: creating },
          cancel: { disabled: creating },
        }}
      >
        <DialogHeader>
          <DialogTitle>Create Dashboard</DialogTitle>
          <DialogDescription>
            Track progress across the Workflows you choose.
          </DialogDescription>
        </DialogHeader>
        <Box
          as="form"
          gap="lg"
          paddingX="lg"
          // Bottom padding keeps the last row off the footer's border —
          // DialogPanel would supply this, but this form composes a bare Box.
          paddingBottom="lg"
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit) void handleCreate();
          }}
        >
          <Field>
            <FieldLabel>Name</FieldLabel>
            <Input
              autoFocus
              disabled={creating}
              onChange={(e) => setName(e.target.value)}
              placeholder="Dashboard"
              value={name}
            />
          </Field>
          <Field>
            <FieldLabel>Location</FieldLabel>
            <WorkflowSelector
              buttonVariant="outline"
              chips
              contentFit
              disabled={creating}
              exclude={archivedIds}
              fill
              maxSelections={0}
              onValueChange={setWorkflowIds}
              placeholder="Select workflows..."
              selectAll
              showChevron
              size="default"
              value={workflowIds}
            />
            {/* Create stays disabled until a source is picked; say why. */}
            {workflowIds.length === 0 ? (
              <FieldDescription>
                Choose at least one workflow to create the dashboard.
              </FieldDescription>
            ) : null}
          </Field>
        </Box>
      </DialogPopup>
    </Dialog>
  );
}
